import React, { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import QRCode from 'qrcode';
import DOMPurify from 'dompurify';
import './Land.css';
//import NoxPayLogo from './NoxPayLogo';
import NoxPayLogo from './NoxPayLogo.svg';
import HistoryScreen from './HistoryScreen';
import POSInitialization from './POSInitialization';
import { showInstallPrompt, isStandalone } from './pwaUtils';
import HeaderInstallButton from './HeaderInstallButton';

const BACKEND_URL = 'https://web3-pos-v2.gdbok.workers.dev';

const POLLING_INTERVALS = {
  PENDING: 4000,          // 2 seconds - waiting for QR scan
  SCANNED: 4000,          // 3 seconds - waiting for source chain selection
  SOURCE_SELECTED: 4000,  // 2 seconds - waiting for signed transaction
  SIGNED: 4000,             // 5 seconds - waiting for relayed
  RELAYED: 4000,             // 5 seconds - waiting for confirmation
  DEFAULT: 4000           // 3 seconds - fallback
};



const TRANSLATIONS = {
  1: { // Spanish
    title: '',
    subtitle: 'Pagos con Criptomonedas',
    amount: 'Monto a Cobrar',
    generateQR: 'Generar QR de Pago',
    amountToCharge: 'Monto a Cobrar',
    ref: 'Ref',
    scanQR: 'Pedir al cliente escanear el código QR',
    waitingPayment: 'Esperando pago del cliente...',
    paymentStatus: 'Estado del Pago',
    chain: 'Cadena',
    sourceChain: 'Red del Cliente',
    transactionId: 'ID de Transacción',
    confirmations: 'Confirmaciones',
    
    // State names
    pending: 'Pendiente',
    scanned: 'Escaneado',
    confirming: 'Confirmando',
    confirmed: 'Confirmado',
    relayed: 'Enviado',
    delayed: 'Demorado',
    cancelled: 'Cancelado',
    
    // Success/Complete
    paymentConfirmed: '¡Pago Confirmado!',
    transactionCompleted: 'Transacción completada exitosamente',
    completeTransaction: 'Completar Transacción',
    
    // Validation messages
    enterAmount: 'Ingrese el monto',
    invalidAmount: 'Monto inválido',
    
    // Menu
    settings: '⚙️ Configuración',
    history: '📊 Historial',
    logout: '🚪 Cerrar sesión',
    
    // QR status
    qrScanned: 'QR Escaneado',
    customerScanning: 'Cliente escaneó QR, esperando pago...',
    
    // Cancelled/Failed
    paymentCancelled: 'Pago Cancelado',
    transactionFailed: 'Transacción fallida',
    
    // Settings
    settingsChanged: 'Configuración Actualizada',
    settingsChangedMsg: 'Las configuraciones del token fueron actualizadas. Reiniciando...',
    posName: '',
    
    // ✅ Error messages (all centralized)
    amountTooSmall: 'Monto muy pequeño. Por favor aumenta la cantidad.',
    pendingPaymentExists: 'Ya existe un pago pendiente. Complétalo o cancélalo primero.',
    createPaymentError: 'Error al crear pago. Intenta de nuevo.',
    networkError: 'Error de red. Verifica tu conexión.',
    rateFetchFailed: 'No se pudo obtener la tasa de cambio. Intenta de nuevo.',
    conversionFailed: 'Error en conversión de moneda. Intenta de nuevo.',
    unsupportedPair: 'Par de conversión no soportado',
    qrGenerationError: 'Error al generar código QR',
    monitoringError: 'Error al iniciar monitoreo',
    generatingPayment: 'Generando...',
    
    // ✅ DELAYED state messages
    paymentDelayed: 'Confirmación Demorada',
    delayedMessage: 'La confirmación está tomando más tiempo de lo esperado.',
    delayedSubtitle: 'Este pago aparecerá en el historial cuando se confirme en la blockchain.',
    backgroundProcessing: 'Procesando en segundo plano',
    pollingTimeout: 'Tiempo de espera agotado',
    
    // Expiration warnings
    paymentExpires: 'El pago expira en',
    seconds: 'segundos',
    paymentExpired: 'Pago expirado',
    
    // Recovery messages
    paymentRecovered: '¡Pago recuperado!',
    pendingPaymentFound: 'Pago pendiente encontrado. Escucha reanudada.',
    
    // Processing stages
    waitingForScan: 'Esperando escaneo del QR',
    waitingForChain: 'Esperando selección de red',
    waitingForSignature: 'Esperando firma',
    waitingForRelay: 'Enviando a blockchain',
    waitingForConfirmation: 'Esperando confirmación'
  },
  2: { // English
    title: '',
    subtitle: 'Crypto Payment Terminal',
    amount: 'Amount to Charge',
    generateQR: 'Generate Payment QR',
    amountToCharge: 'Amount to Charge',
    ref: 'Ref',
    scanQR: 'scan QR code',
    waitingPayment: 'Waiting for customer payment...',
    paymentStatus: 'Payment Status',
    chain: 'Chain',
    sourceChain: 'Customer Chain',
    transactionId: 'Transaction ID',
    confirmations: 'Confirmations',
    
    // State names
    pending: 'Pending',
    scanned: 'Scanned',
    confirming: 'Confirming',
    confirmed: 'Confirmed',
    relayed: 'Relayed',
    delayed: 'Delayed',
    cancelled: 'Cancelled',
    
    // Success/Complete
    paymentConfirmed: 'Payment Confirmed!',
    transactionCompleted: 'Transaction completed successfully',
    completeTransaction: 'Complete Transaction',
    
    // Validation messages
    enterAmount: 'Enter amount',
    invalidAmount: 'Invalid amount',
    
    // Menu
    settings: '⚙️ Settings',
    history: '📊 History',
    logout: '🚪 Logout',
    
    // QR status
    qrScanned: 'QR Scanned',
    customerScanning: 'Customer scanned QR, waiting for payment...',
    
    // Cancelled/Failed
    paymentCancelled: 'Payment Cancelled',
    transactionFailed: 'Transaction failed',
    
    // Settings
    settingsChanged: 'Settings Updated',
    settingsChangedMsg: 'Token settings were updated. Restarting...',
    posName: '',
    
    // ✅ Error messages (all centralized)
    amountTooSmall: 'Amount too small. Please increase amount.',
    pendingPaymentExists: 'Pending payment exists. Complete or cancel it first.',
    createPaymentError: 'Failed to create payment. Please try again.',
    networkError: 'Network error. Check your connection.',
    rateFetchFailed: 'Failed to fetch exchange rate. Please try again.',
    conversionFailed: 'Currency conversion error. Please try again.',
    unsupportedPair: 'Unsupported currency pair',
    qrGenerationError: 'Error generating QR code',
    monitoringError: 'Failed to start payment monitoring',
    generatingPayment: 'Generating...',
    
    // ✅ DELAYED state messages
    paymentDelayed: 'Confirmation Delayed',
    delayedMessage: 'Confirmation is taking longer than expected.',
    delayedSubtitle: 'This payment will appear in history when confirmed on blockchain.',
    backgroundProcessing: 'Processing in background',
    pollingTimeout: 'Polling timeout',
    
    // Expiration warnings
    paymentExpires: 'Payment expires in',
    seconds: 'seconds',
    paymentExpired: 'Payment expired',
    
    // Recovery messages
    paymentRecovered: 'Payment recovered!',
    pendingPaymentFound: 'Pending payment found. Listening resumed.',
    
    // Processing stages
    waitingForScan: 'Waiting for QR scan',
    waitingForChain: 'Waiting for chain selection',
    waitingForSignature: 'Waiting for signature',
    waitingForRelay: 'Sending to blockchain',
    waitingForConfirmation: 'Waiting for confirmation'
  }
};

function Qrcode() {
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const thelang = urlParams.get('lang');

  const [lang, setLang] = useState(thelang === '2' ? 2 : 1);
  const t = TRANSLATIONS[lang];

  // POS Initialization State
  const [isPOSInitialized, setIsPOSInitialized] = useState(false);
  const [posSettings, setPosSettings] = useState(null);
  const [checkingInitialization, setCheckingInitialization] = useState(true);

  // Payment Flow State
  const [currentScreen, setCurrentScreen] = useState('setup');
  const [amount, setAmount] = useState('');
  const [amountDisplay, setAmountDisplay] = useState('0.00');
  const [sourceChainId, setSourceChainId] = useState('');
  const [txReference, setTxReference] = useState('');

  // ✅ NEW: Conversion data state
  const [conversionRate, setConversionRate] = useState(null);
  const [cryptoAmount, setCryptoAmount] = useState(null);

  const [qr, setQr] = useState('');
  const [listening, setListening] = useState(false);
  const [qrScanned, setQrScanned] = useState(false);
  const [qrExpired, setQrExpired] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [message, setMessage] = useState('');
  const [txData, setTxData] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [isCreatingPayment, setIsCreatingPayment] = useState(false);

  const pollIntervalRef = useRef(null);
 
  const [paymentState, setPaymentState] = useState(null);

// Lock scroll only inside the POS container (no body, no ref)
useEffect(() => {
  const land = document.querySelector('.Land');
  if (!land) return;

  if (isPOSInitialized) {
    land.classList.add('pos-active');
  } else {
    land.classList.remove('pos-active');
  }

  return () => {
    land.classList.remove('pos-active');
  };
}, [isPOSInitialized]);





  // Check if POS is already initialized
  useEffect(() => {
    const checkInitialization = () => {
      const initialized = localStorage.getItem('pos_initialized');
      const settingsStr = localStorage.getItem('pos_settings');

      if (initialized === 'true' && settingsStr) {
        try {
          const settings = JSON.parse(settingsStr);
          setPosSettings(settings);
          setIsPOSInitialized(true);
          console.log('✅ POS already initialized:', settings);
        } catch (err) {
          console.error('Failed to parse POS settings:', err);
          localStorage.removeItem('pos_initialized');
          localStorage.removeItem('pos_settings');
        }
      }

      setCheckingInitialization(false);


    };

    checkInitialization();
  }, []);

  // Handle POS initialization
  const handlePOSInitialized = (settings) => {
    setPosSettings(settings);
    setIsPOSInitialized(true);
  };

  // Handle POS reset
  const handleResetPOS = () => {
    if (window.confirm('Reset POS? This will clear all settings.')) {
      localStorage.removeItem('pos_initialized');
      localStorage.removeItem('pos_settings');
      localStorage.removeItem('pendingPayment');
      setPosSettings(null);
      setIsPOSInitialized(false);
      window.location.reload();
    }
  };

// Check for pending payment on mount
useEffect(() => {
  const checkPendingPayment = async () => {
    // ✅ FIX: Only check initialization status, not screen
    if (!isPOSInitialized || !posSettings) {
      console.log('⏳ Waiting for POS initialization before checking pending payments');
      return;
    }

    const pending = localStorage.getItem('pendingPayment');
    if (!pending) {
      console.log('ℹ️ No pending payment found');
      return;
    }

    try {
      const payment = JSON.parse(pending);

      // ✅ Check if payment is too old (24 hours)
      if (Date.now() - payment.timestamp > 24 * 60 * 60 * 1000) {
        console.log('🗑️ Removing stale payment (>24h old)');
        localStorage.removeItem('pendingPayment');
        return;
      }

      console.log('🔍 Found pending payment, checking backend...', payment.txRef);

      // ✅ Check backend status
      const response = await fetch(`${BACKEND_URL}/api/check-payment/${payment.txRef}`);
      const result = await response.json();

      if (!result.found) {
        console.log('❌ Payment not found in backend, removing from storage');
        localStorage.removeItem('pendingPayment');
        return;
      }

      if (result.state === 'EXPIRED') {
        console.log('⏰ Payment expired, removing from storage');
        localStorage.removeItem('pendingPayment');
        return;
      }

      // ✅ Restore conversion data if available
      if (payment.conversionRate) {
        console.log('💱 Restoring conversion data');
        setConversionRate(payment.conversionRate);
        setCryptoAmount(payment.cryptoAmount);
      }

      // ✅ Restore basic payment info
      setTxReference(payment.txRef);
      setAmount(payment.amount);
      setAmountDisplay(payment.amountDisplay);

      // ✅ Handle CONFIRMED payments
      if (result.state === 'CONFIRMED') {
        console.log('✅ Recovering completed/confirming payment');
        
        const statusText = result.state === 'CONFIRMED'
          ? (lang === 1 ? 'Confirmado' : 'Confirmed')
          : (lang === 1 ? 'Detectado' : 'Detected');

        setTxData({
          id: result.txHash || 'Unknown',
          sender: 'Unknown',
          hash: result.txHash || 'Unknown',
          status: result.state === 'CONFIRMED' ? 'confirmed' : 'pending',
          statusText: statusText,
          confirmations: result.confirmations || 0,
          sourceChain: result.sourceChain,
          token: result.genericToken,
          genericToken: result.genericToken,
          amount: payment.amountDisplay || payment.amount
        });

        setCurrentScreen('status');
        setConfirmed(result.state === 'CONFIRMED');
        
        if (result.state === 'CONFIRMED') {
          localStorage.removeItem('pendingPayment');
        //} else {
          // Continue polling if still PAID
        //  startPollingPayment(payment.txRef);
        }

        setMessage(lang === 1 ? '¡Pago recuperado!' : 'Payment recovered!');
        setTimeout(() => setMessage(''), 5000);

      } else if (result.state === 'PENDING' || result.state === 'SCANNED' || result.state === 'SOURCE_SELECTED' || result.state === 'SIGNED' || result.state === 'RELAYED') {
        console.log('🔄 Recovering pending payment, resuming polling');
        
        // ✅ Restore QR image if available
        if (payment.qrImage) {
          console.log('🖼️ Restoring QR code image');
          setQr(payment.qrImage);
        }
        
        setCurrentScreen('payment');
        setListening(true);
        setQrScanned(result.state === 'SCANNED' || result.state === 'SOURCE_SELECTED' || result.state === 'SIGNED' || result.state === 'RELAYED');

        startPollingPayment(payment.txRef);

        setMessage(lang === 1
          ? 'Pago pendiente encontrado. Escucha reanudada.'
          : 'Pending payment found. Listening resumed.'
        );
        setTimeout(() => setMessage(''), 5000);
      }

    } catch (error) {
      console.error('❌ Error checking pending payment:', error);
      
      // ✅ Don't remove payment on network errors, user can retry
      if (error.message?.includes('JSON')) {
        // Only remove if localStorage data is corrupted
        console.log('🗑️ Corrupted payment data, removing');
        localStorage.removeItem('pendingPayment');
      } else {
        // Network error - keep the payment, show message
        setMessage(lang === 1 
          ? 'Error al recuperar pago. Verifica tu conexión.' 
          : 'Error recovering payment. Check connection.'
        );
      }
    }
  };

  // ✅ FIX: Run check only when POS is fully initialized
  if (isPOSInitialized && posSettings) {
    console.log('🚀 POS initialized, checking for pending payments...');
    const timeoutId = setTimeout(checkPendingPayment, 500);
    return () => clearTimeout(timeoutId);
  }
  
}, [isPOSInitialized, posSettings, lang]); // ✅ Added lang to dependencies



// Cleanup on unmount
useEffect(() => {
  return () => {
    console.log('🧹 Component unmounting, cleaning up polling');
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };
}, []);

  
useEffect(() => {
  console.log('📺 Screen changed to:', currentScreen);
  
  // If returning to setup, cleanup any active polling
  if (currentScreen === 'setup' && pollIntervalRef.current) {
    console.log('🧹 Returning to setup, cleaning up polling');
    clearInterval(pollIntervalRef.current);
    pollIntervalRef.current = null;
  }
  
  // Scroll to top when changing screens
  if (typeof window !== 'undefined') {
    window.scrollTo(0, 0);
  }
}, [currentScreen]);


  // Show initialization screen if not initialized
  if (checkingInitialization) {
    return (
      <div className="Land">
        <header className="Land-header">
   
        </header>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
          color: '#6b7280'
        }}>
          Loading...
        </div>
      </div>
    );
  }

  if (!isPOSInitialized) {
    return (
<div className="Land">
      <header className="Land-header">
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          width: '100%',
          padding: '12px 0'
        }}>
          {/* Left side: Logo and Title */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px',
            flex: 1
          }}>
            

  


<img src={NoxPayLogo} alt="NOX Logo" width={120}
  height={40}/>

            
            <h1 style={{ 
              margin: 0,
              fontSize: '24px',
              fontWeight: 'bold'
            }}>
              {t.title}
            </h1>
          </div>

          {/* Right side: Language and Install buttons */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '10px'
          }}>
            {/* Language Toggle Button */}
            <button 
              onClick={() => setLang(lang === 1 ? 2 : 1)}
              title={lang === 1 ? 'Switch to English' : 'Cambiar a Español'}
              style={{
                width: '48px',
                height: '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(255, 255, 255, 0.15)',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 'bold',
                transition: 'all 0.2s ease',
                color: 'white'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.25)';
                e.target.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.15)';
                e.target.style.transform = 'scale(1)';
              }}
            >
              {lang === 1 ? 'EN' : 'ES'}
            </button>

            {/* Install PWA Button */}
            <HeaderInstallButton lang={lang} />
          </div>
        </div>
      </header>

        <POSInitialization onInitialized={handlePOSInitialized} lang={lang} onLangChange={setLang} />
      </div>
    );
  }


  // Amount input handlers
  const addDigit = (digit) => {
    if (digit === '.') {
      if (amount.includes('.')) return;
      if (amount === '') {
        setAmount('0.');
        setAmountDisplay('0.');
        return;
      }
      const newAmount = amount + '.';
      setAmount(newAmount);
      setAmountDisplay(newAmount);
      setMessage('');
      return;
    }

    const hasDecimal = amount.includes('.');
    if (hasDecimal) {
      const afterDecimal = amount.split('.')[1] || '';
      if (afterDecimal.length >= 2) return;
    }

    const newAmount = amount + digit;
    setAmount(newAmount);
    setAmountDisplay(newAmount);
    setMessage('');
  };

  const clearAmount = () => {
    const newAmount = amount.slice(0, -1);
    setAmount(newAmount);
    setAmountDisplay(newAmount || '0.00');
    setMessage('');
  };

  // Validate form before payment creation
  const validateForm = () => {
    if (!posSettings) {
      setMessage('POS settings not loaded');
      return false;
    }

    if (!amount || amount === '' || parseFloat(amount) <= 0) {
      setMessage(t.enterAmount);
      return false;
    }

    return true;
  };

  // Get token name from generic token type
  const getTokenName = (genericToken) => {
    return genericToken || 'Unknown';
  };

  // Generate QR Code and create payment in backend
const generateQRCode = async () => {
  setMessage('');

  if (isCreatingPayment) {
    console.log('⛔ Payment creation already in progress');
    return;
  }

  if (!validateForm()) return;

  setIsCreatingPayment(true);

  const displayAmount = parseFloat(amount).toFixed(2);
  setAmountDisplay(displayAmount);

  try {
    console.log(`🔵 Creating payment in backend with ${displayAmount} ${posSettings.fiatCurrency}...`);

    const response = await fetch(`${BACKEND_URL}/api/create-payment`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        tokenId: posSettings.tokenId,
        posId: posSettings.posId,
        amount: displayAmount // ✅ Now sending FIAT amount
      })
    });


// Replace this section in your generateQRCode function (around line 625-640):

const result = await response.json();

if (!response.ok) {
  // ✅ Handle specific error codes with bilingual messages
  let errorMessage = '';
  
  switch (result.code) {
    case 'AMOUNT_TOO_SMALL':
      errorMessage = lang === 1 
        ? `Monto muy pequeño. Mínimo ${result.minimum} ${result.currency}`
        : `Amount too small. Minimum ${result.minimum} ${result.currency}`;
      break;
      
    case 'AMOUNT_TOO_LARGE':
      errorMessage = lang === 1 
        ? `Monto muy grande. Máximo ${result.maximum} ${result.currency}`
        : `Amount too large. Maximum ${result.maximum} ${result.currency}`;
      break;
      
    case 'AMOUNT_ZERO':
      errorMessage = lang === 1 
        ? 'El monto no puede ser cero'
        : 'Amount cannot be zero';
      break;
      
    case 'INVALID_AMOUNT_FORMAT':
      errorMessage = lang === 1 
        ? 'Formato de monto inválido. Use: 10.74'
        : 'Invalid amount format. Use: 10.74';
      break;
      
    case 'POS_HAS_PENDING_PAYMENT':
      errorMessage = lang === 1 
        ? 'Ya existe un pago pendiente. Complétalo o cancélalo primero.'
        : 'Pending payment exists. Complete or cancel it first.';
      break;
      
    case 'TOKEN_NOT_FOUND':
      errorMessage = lang === 1 
        ? 'Token no encontrado. Reinicia el POS.'
        : 'Token not found. Reset POS.';
      break;
      
    case 'TOKEN_INACTIVE':
      errorMessage = lang === 1 
        ? 'Token inactivo. Contacta soporte.'
        : 'Token inactive. Contact support.';
      break;
      
    case 'RATE_FETCH_FAILED':
      errorMessage = lang === 1 
        ? 'No se pudo obtener la tasa de cambio. Intenta de nuevo.'
        : 'Failed to fetch exchange rate. Please try again.';
      break;
      
    case 'CONVERSION_FAILED':
      errorMessage = lang === 1 
        ? 'Error en conversión de moneda. Intenta de nuevo.'
        : 'Currency conversion error. Please try again.';
      break;
      
    case 'UNSUPPORTED_PAIR':
      errorMessage = lang === 1 
        ? `Par de conversión no soportado: ${result.pair}`
        : `Unsupported currency pair: ${result.pair}`;
      break;
      
    case 'COUNTER_GENERATION_FAILED':
      errorMessage = lang === 1 
        ? 'Error al generar referencia. Intenta de nuevo.'
        : 'Failed to generate reference. Please try again.';
      break;
      
    case 'PAYMENT_CREATION_FAILED':
      errorMessage = lang === 1 
        ? 'Error al crear pago. Intenta de nuevo.'
        : 'Failed to create payment. Please try again.';
      break;
      
    default:
      // Fallback to backend error message or generic error
      errorMessage = result.error || result.message || (lang === 1 
        ? 'Error al crear pago. Intenta de nuevo.'
        : 'Failed to create payment. Please try again.');
  }
  
  setMessage(errorMessage);
  setIsCreatingPayment(false);
  return;
}


if (!result.success) {
  setMessage(result.error || (lang === 1 ? 'Error al crear pago' : 'Failed to create payment'));
  setIsCreatingPayment(false);
  return;
}
   

    const txRef = result.txRef;
    setTxReference(txRef);

    // ✅ NEW: Store conversion data
    setConversionRate(result.payment.conversionRate);
    setCryptoAmount(result.payment.cryptoAmount);

    console.log('✅ Payment created with txRef:', txRef);
    console.log(`💱 Conversion: ${displayAmount} ${posSettings.fiatCurrency} = ${result.payment.cryptoAmount}`);
    console.log(`📊 Rate: ${result.payment.conversionRateString}`);

    // Generate QR code with payment data
    const jsonData = JSON.stringify({
      txRef: txRef,
      type: 'PAYMENT'
    });

    QRCode.toDataURL(jsonData, {
      width: 400,
      margin: 2,
      color: { dark: '#335383FF', light: '#EEEEEEFF' }
    }, async (err, url) => {
      if (err) {
        setMessage('Error generating QR code');
        setIsCreatingPayment(false);
        return;
      }

      setQr(url);
      setCurrentScreen('payment');

      // ✅ Store QR image, FIAT amount, and conversion data to localStorage
      localStorage.setItem('pendingPayment', JSON.stringify({
        txRef: txRef,
        amount: result.payment.cryptoAmount, // Crypto amount for backend
        amountDisplay: displayAmount, // Fiat amount for display
        fiatAmount: displayAmount,
        fiatCurrency: posSettings.fiatCurrency,
        cryptoAmount: result.payment.cryptoAmount,
        conversionRate: result.payment.conversionRate,
        qrImage: url,
        genericToken: posSettings.genericToken,
        timestamp: Date.now()
      }));

      // Start listening for payment
      await startListening(txRef);
      setIsCreatingPayment(false);
    });

} catch (error) {
    console.error('❌ Create payment error:', error);
    setMessage(lang === 1 ? 'Error de red. Intenta de nuevo.' : 'Network error. Please try again.');
  } finally {
    setIsCreatingPayment(false);  // ✅ Always resets flag
  }
};

  // Start listening for payment updates
  const startListening = async (txRef) => {
    setListening(true);
    setQrScanned(false);

    try {
      // Check current payment state
      const checkResponse = await fetch(`${BACKEND_URL}/api/check-payment/${txRef}`);
      const checkResult = await checkResponse.json();

      if (checkResult.found && (checkResult.state === 'PENDING' || checkResult.state === 'SCANNED' || checkResult.state === 'SOURCE_SELECTED' || checkResult.state === 'SIGNED' || checkResult.state === 'RELAYED')) {
        if (checkResult.state === 'SCANNED') {
          setQrScanned(true);
        }
        startPollingPayment(txRef);
        return;
      }

      if (checkResult.found && (checkResult.state === 'CONFIRMED')) {
        handlePaymentCompleted(checkResult, txRef);
        return;
      }

      startPollingPayment(txRef);

    } catch (error) {
      console.error('Failed to start listening:', error);
      setMessage('Failed to start payment monitoring');
      setListening(false);
    }
  };




// 🔄 Replace your startPollingPayment function with this updated version
const startPollingPayment = (txRef) => {
  if (pollIntervalRef.current) {
    clearInterval(pollIntervalRef.current);
    pollIntervalRef.current = null;
  }

  console.log('🔄 Starting payment polling for:', txRef);

  let currentInterval = POLLING_INTERVALS.DEFAULT;
  let pollCount = 0;
  const maxPolls = 200;

  const pollFunction = async () => {
    try {
      pollCount++;
      
      if (pollCount > maxPolls) {
        console.warn('⚠️ Max polling attempts reached, stopping');
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        setMessage(t.pollingTimeout);

        setTimeout(() => {
          resetTransaction();
        }, 2000);

        return;
      }

      const response = await fetch(`${BACKEND_URL}/api/check-payment/${txRef}`);
      const result = await response.json();

      console.log(`📊 Poll #${pollCount}:`, result.state);

      // Show expiration warning
      if (result.timeRemaining && result.timeRemaining < 30000 && result.timeRemaining > 0) {
        const secondsRemaining = Math.ceil(result.timeRemaining / 1000);
        if (secondsRemaining <= 30 && secondsRemaining % 10 === 0) {
          setMessage(`⏰ ${t.paymentExpires} ${secondsRemaining} ${t.seconds}`);
        }
      } else {
        setMessage('');
      }

      if (!result.found) {
        console.warn('⚠️ Payment not found, stopping poll');
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        resetTransaction();
        return;
      }

      // ✅ Update payment state for badge display
      setPaymentState(result.state);

      // Adjust polling interval
      let newInterval = POLLING_INTERVALS.DEFAULT;
      
      switch (result.state) {
        case 'PENDING':
          newInterval = POLLING_INTERVALS.PENDING;
          break;
        case 'SCANNED':
          newInterval = POLLING_INTERVALS.SCANNED;
          setQrScanned(true);
          break;
        case 'SOURCE_SELECTED':
          newInterval = POLLING_INTERVALS.SOURCE_SELECTED;
          setQrScanned(true);
          break;
        case 'SIGNED':
          newInterval = POLLING_INTERVALS.SIGNED;
          break;
        case 'RELAYED':
          newInterval = POLLING_INTERVALS.RELAYED;
          break;
      }

      // Restart interval if speed changed
      if (newInterval !== currentInterval) {
        console.log(`⚡ Adjusting poll interval: ${currentInterval}ms → ${newInterval}ms`);
        currentInterval = newInterval;
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = setInterval(pollFunction, newInterval);
      }

      // ✅ Handle CONFIRMED state
      if (result.state === 'CONFIRMED') {
        console.log('✅ Payment CONFIRMED');
        
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        localStorage.removeItem('pendingPayment');

        setListening(false);
        setQr('');
        setConfirmed(true);

        setTxData(prev => {
          const pending = localStorage.getItem('pendingPayment');
          let decimalAmount = '';
          
          if (pending) {
            try {
              const payment = JSON.parse(pending);
              decimalAmount = payment.amountDisplay || payment.amount;
            } catch (e) {
              console.error('Failed to parse pending payment:', e);
            }
          }

          const baseData = {
            id: result.txHash,
            sender: 'Unknown',
            hash: result.txHash,
            status: 'confirmed',
            statusText: t.confirmed,
            confirmations: result.confirmations || 0,
            sourceChain: result.sourceChain,
            genericToken: result.genericToken,
            amount: decimalAmount || prev?.amount || ''
          };

          return prev ? { ...prev, ...baseData } : baseData;
        });

        setCurrentScreen('status');
      }

      // ✅ NEW: Handle DELAYED state (terminal - stop polling)
      if (result.state === 'DELAYED') {
        console.log('⏱️ Payment DELAYED - stopping poll');
        
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        localStorage.removeItem('pendingPayment');

        setTxData({
          id: result.txHash || 'Processing',
          sender: 'Unknown',
          hash: result.txHash || 'Processing',
          status: 'delayed',
          statusText: t.delayed,
          confirmations: result.confirmations || 0,
          sourceChain: result.sourceChain,
          genericToken: result.genericToken,
          amount: amountDisplay,
          delayedAt: result.delayedAt
        });

        setCurrentScreen('status');
        setListening(false);
        setQr('');
        setConfirmed(false);
      }

      // ✅ Handle CANCELLED state
      if (result.state === 'CANCELLED') {
        console.log('❌ Payment CANCELLED');
        
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        localStorage.removeItem('pendingPayment');

        let failureReason = t.transactionFailed;
        if (result.cancelReason) {
          // Keep original reason if technical
          if (result.cancelReason.includes('Max confirmation') || 
              result.cancelReason.includes('reverted') ||
              result.cancelReason.includes('Gelato')) {
            failureReason = result.cancelReason;
          }
        }

        setTxData({
          id: result.txHash || 'Unknown',
          sender: 'Unknown',
          hash: result.txHash || 'Unknown',
          status: 'cancelled',
          statusText: t.cancelled,
          confirmations: result.confirmations || 0,
          sourceChain: result.sourceChain,
          genericToken: result.genericToken,
          failureReason: failureReason
        });

        setCurrentScreen('status');
        setListening(false);
        setQr('');

        setMessage(failureReason);
        setTimeout(() => {
          resetTransaction();
        }, 3000);
      }

      // ✅ Handle EXPIRED state
      if (result.state === 'EXPIRED') {
        console.log('⏰ Payment EXPIRED');
        
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        localStorage.removeItem('pendingPayment');
        
        setListening(false);
        setQrExpired(true);
        
        setMessage(t.paymentExpired);
        
        setTimeout(() => {
          resetTransaction();
        }, 2000);
      }

    } catch (error) {
      console.error('❌ Polling error:', error);
      // Don't stop polling on network errors
    }
  };

  // Start polling immediately
  pollFunction();
  pollIntervalRef.current = setInterval(pollFunction, currentInterval);
};


const handlePaymentCompleted = (result, txRef) => {
  console.log('🎉 handlePaymentCompleted called', result.state);
  
  const pending = localStorage.getItem('pendingPayment');
  let decimalAmount = amountDisplay;
  
  if (pending) {
    try {
      const payment = JSON.parse(pending);
      decimalAmount = payment.amountDisplay || payment.amount;
    } catch (e) {
      console.error('Failed to parse pending payment:', e);
    }
  }
  
  // Only clear when moving to CONFIRMED
  if (result.state === 'CONFIRMED') {
    localStorage.removeItem('pendingPayment');
  }

  const statusText = result.state === 'CONFIRMED'
    ? (lang === 1 ? 'Confirmado' : 'Confirmed')
    : (lang === 1 ? 'Detectado' : 'Detected');

  setTxData({
    id: result.txHash,
    sender: 'Unknown',
    hash: result.txHash,
    status: result.state === 'CONFIRMED' ? 'confirmed' : 'pending',
    statusText: statusText,
    confirmations: result.confirmations || 0,
    sourceChain: result.sourceChain,
    genericToken: result.genericToken,
    amount: decimalAmount
  });

  setListening(true);
  setQr('');
  setCurrentScreen('status');
  
  // ✅ FIX: If confirmed, update confirmed state
  if (result.state === 'CONFIRMED') {
    setConfirmed(true);
  }
};

  // Reset transaction
  const resetTransaction = async () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    localStorage.removeItem('pendingPayment');

    setCurrentScreen('setup');
    setSourceChainId('');
    setAmount('');
    setAmountDisplay('0.00');
    setTxReference('');
    setQr('');
    setListening(false);
    setQrScanned(false);
    setQrExpired(false);
    setConfirmed(false);
    setTxData(null);
    setMessage('');
  };

  // Helper functions
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'pending': return 'status-badge status-pending';
      case 'confirming': return 'status-badge status-confirming';
      case 'confirmed': return 'status-badge status-confirmed';
      case 'cancelled': return 'status-badge status-cancelled';
      default: return 'status-badge status-pending';
    }
  };

  return (
    <div className="Land">

<header className="Land-header">
  <div style={{ 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'space-between', 
    width: '100%',
    padding: '12px 0'
  }}>
    {/* Left side: Logo and Title */}
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: '12px',
      flex: 1
    }}>

  

<img src={NoxPayLogo} alt="NOX Logo" width={120}
  height={40}/>



      <h1 style={{ 
        margin: 0,
        fontSize: '24px',
        fontWeight: 'bold'
      }}>
        {t.title}
      </h1>
    </div>

    {/* Right side: Action Buttons (only show on setup screen) */}
    {currentScreen === 'setup' && (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '10px'
      }}>
        {/* History Button */}
        <button
          className="header-icon-btn"
          onClick={() => setShowHistory(true)}
          title={t.history}
          style={{
            width: '48px',
            height: '48px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(255, 255, 255, 0.15)',
            border: '2px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '12px',
            cursor: 'pointer',
            fontSize: '20px',
            transition: 'all 0.2s ease',
            color: 'white'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.25)';
            e.target.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.15)';
            e.target.style.transform = 'scale(1)';
          }}
        >
          📊
        </button>

        {/* Reset Button */}
        <button
          className="header-icon-btn"
          onClick={handleResetPOS}
          title="Reset POS"
          style={{
            width: '48px',
            height: '48px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(255, 255, 255, 0.15)',
            border: '2px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '12px',
            cursor: 'pointer',
            fontSize: '20px',
            transition: 'all 0.2s ease',
            color: 'white'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.25)';
            e.target.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.15)';
            e.target.style.transform = 'scale(1)';
          }}
        >
          🔄
        </button>
      
        {/* Language Toggle Button */}
        <button 
          className="header-icon-btn"
          onClick={() => setLang(lang === 1 ? 2 : 1)}
          title={lang === 1 ? 'Switch to English' : 'Cambiar a Español'}
          style={{
            width: '48px',
            height: '48px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(255, 255, 255, 0.15)',
            border: '2px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '12px',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: 'bold',
            transition: 'all 0.2s ease',
            color: 'white'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.25)';
            e.target.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.15)';
            e.target.style.transform = 'scale(1)';
          }}
        >
          {lang === 1 ? 'EN' : 'ES'}
        </button>

        {/* Install PWA Button */}
        <HeaderInstallButton lang={lang} />
      </div>
  
  )}

</div>



  {/* POS Info Bar */}
  {posSettings && (
    <div style={{
      marginTop: '8px',
      fontSize: '12px',
      color: 'rgba(255,255,255,0.9)',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      fontWeight: '500'
    }}>
      <span>
        {t.posName} <strong>{posSettings.tokenName}</strong>
      </span>
      <span style={{ opacity: 0.5 }}>•</span>
      <span>{posSettings.fiatCurrency}</span>
      <span style={{ opacity: 0.5 }}>➜</span>
      <span>{getTokenName(posSettings.genericToken)}</span>
    </div>
  )}
</header>

      {message && (
        <div className="alert">
          <span>⚠️</span>
          <span>{message}</span>
        </div>
      )}

      {currentScreen === 'setup' && (
        <div className="pos-content">
          <div className="setup-screen">
            <div className="setup-step">
              <div className="form-group">
                <label className="form-label">
    
                  <span>{t.amount}</span>
                  <span style={{
                    fontSize: '11px',
                    color: '#6366f1',
                    fontWeight: '500',
                    background: '#eef2ff',
                    padding: '4px 8px',
                    borderRadius: '4px'
                  }}>
                    {posSettings.fiatCurrency}
                  </span>
                </label>
                <input
                  type="text"
                  className="form-control amount-input-display"
                  value={amountDisplay}
                  readOnly
                />

                <div className="keypad">
                  <button className="keypad-btn" onClick={() => addDigit('1')}>1</button>
                  <button className="keypad-btn" onClick={() => addDigit('2')}>2</button>
                  <button className="keypad-btn" onClick={() => addDigit('3')}>3</button>
                  <button className="keypad-btn" onClick={() => addDigit('4')}>4</button>
                  <button className="keypad-btn" onClick={() => addDigit('5')}>5</button>
                  <button className="keypad-btn" onClick={() => addDigit('6')}>6</button>
                  <button className="keypad-btn" onClick={() => addDigit('7')}>7</button>
                  <button className="keypad-btn" onClick={() => addDigit('8')}>8</button>
                  <button className="keypad-btn" onClick={() => addDigit('9')}>9</button>
                  <button className="keypad-btn special" onClick={() => addDigit('.')}>.</button>
                  <button className="keypad-btn" onClick={() => addDigit('0')}>0</button>
                  <button className="keypad-btn special" onClick={clearAmount}>⌫</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}



{currentScreen === 'payment' && (
  <>
    {/* Responsive container for desktop */}
    <div className={window.innerWidth >= 768 ? 'payment-screen-container' : ''}>
      
      {/* Floating Amount Card */}
      <div className="floating-amount-card">
        {/* Primary: Fiat Amount */}
        <div style={{
          fontSize: '40px',
          fontWeight: '800',
          color: '#6366f1',
          fontFamily: "'SF Mono', Monaco, monospace",
          lineHeight: '1',
          marginBottom: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px'
        }}>
          <span style={{ fontSize: '36px', opacity: 0.8 }}>
            {posSettings.fiatCurrency === 'EUR' ? '€' : '$'}
          </span>
          {amountDisplay}
          <span style={{ fontSize: '18px', fontWeight: '600', opacity: 0.7 }}>
            {posSettings.fiatCurrency}
          </span>
        </div>

        {/* Secondary: Crypto Amount */}
        {cryptoAmount && (
          <div style={{
            fontSize: '16px',
            color: '#6b7280',
            fontWeight: '500',
            marginBottom: '16px'
          }}>
            ≈ {cryptoAmount} {posSettings.genericToken} @ {conversionRate.toFixed(2)} {posSettings.fiatCurrency}
          </div>
        )}

        {/* Reference Number */}
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          background: 'rgba(99, 102, 241, 0.1)',
          padding: '8px 16px',
          borderRadius: '12px',
          fontSize: '10px',
          fontWeight: '600',
          color: '#6366f1',
          marginBottom: '12px'
        }}>
          <span>Ref:</span>
          <span style={{ fontFamily: 'monospace', letterSpacing: '1px' }}>
            {txReference}
          </span>
        </div>

        {/* ✅ NEW: State Progress Badges */}
        <div className="state-badge-container">
          <div className={`state-badge state-badge-pending ${
            paymentState === 'PENDING' ? 'state-badge-active state-badge-pulse' : 'state-badge-completed'
          }`}>
            {paymentState === 'PENDING' && '●'} {t.pending}
          </div>
          
          {['SCANNED', 'SOURCE_SELECTED', 'SIGNED', 'RELAYED'].includes(paymentState) && (
            <div className={`state-badge state-badge-scanned ${
              ['SCANNED', 'SOURCE_SELECTED', 'SIGNED'].includes(paymentState) ? 'state-badge-active state-badge-pulse' : 'state-badge-completed'
            }`}>
              {['SCANNED', 'SOURCE_SELECTED', 'SIGNED'].includes(paymentState) && '●'} {t.scanned}
            </div>
          )}
          
          {['RELAYED'].includes(paymentState) && (
            <div className="state-badge state-badge-relayed state-badge-active state-badge-pulse">
              ● {t.relayed}
            </div>
          )}
        </div>
      </div>

      {/* QR Code Section */}
      
        <div className={window.innerWidth >= 768 ? 'qr-section-wrapper' : 'pos-content'} style={{
        paddingTop: window.innerWidth >= 768 ? '0' : '0',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flex: window.innerWidth >= 768 ? 1 : 'auto'
      }}>
        <div className="qr-display-enhanced">
          <img
            src={qr}
            alt="QR Code"
            style={{
              width: '50%',
              maxWidth: window.innerWidth >= 320 ? '320px' : '280px',
              height: '50%'
            }}
          />
        </div>

        {/* Status indicator text */}
        <p style={{
          textAlign: 'center',
          color: '#6b7280',
          fontSize: '14px',
          marginTop: '20px',
          fontWeight: '500'
        }}>
          {qrScanned 
            ? (lang === 1 ? '✓ Cliente procesando pago...' : '✓ Customer processing payment...') 
            : t.scanQR
          }
        </p>
      </div>
    </div>
  </>
)}


{currentScreen === 'status' && txData && (
  <div className="confirmation-screen-wrapper">
    
    {/* ✅ CONFIRMED State - Enhanced Animation */}
    {txData.status === 'confirmed' && (
      <div className="success-animation-enhanced">
        <div className="success-checkmark-enhanced">
          <div className="check-icon">✓</div>
        </div>
        <h2 className="success-title-enhanced">{t.paymentConfirmed}</h2>
        <p className="success-message-enhanced">{t.transactionCompleted}</p>
      </div>
    )}

    {/* ✅ NEW: DELAYED State - Processing Animation */}
    {txData.status === 'delayed' && (
      <div className="delayed-animation">
        <div className="delayed-icon-container">
          <div className="delayed-icon">⏱</div>
        </div>
        <h2 className="delayed-title">{t.paymentDelayed}</h2>
        <p className="delayed-message">{t.delayedMessage}</p>
        <p className="delayed-subtitle">{t.delayedSubtitle}</p>
        <div className="delayed-processing-indicator">
          {t.backgroundProcessing}
        </div>
      </div>
    )}

    {/* ✅ CANCELLED State */}
    {txData.status === 'cancelled' && (
      <div className="delayed-animation">
        <div className="delayed-icon-container">
          <div className="delayed-icon" style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' }}>
            ✕
          </div>
        </div>
        <h2 className="delayed-title" style={{ color: '#dc2626' }}>
          {t.paymentCancelled}
        </h2>
        <p className="delayed-message" style={{ color: '#6b7280' }}>
          {txData.failureReason}
        </p>
      </div>
    )}




 {/* Status Card - Enhanced */}
    <div className="status-card-enhanced">
      <div className="status-header">
        <span className="status-title">{t.paymentStatus}</span>
        <span className={getStatusBadgeClass(txData.status)}>
          {txData.statusText}
        </span>
      </div>








 {/* Amount Display */}
      <div className="status-detail-enhanced">
        <span className="status-label-enhanced">{t.amountToCharge}:</span>
        <div className="status-amount-display">
          <span className="status-amount-primary">
            {posSettings.fiatCurrency === 'EUR' ? '€' : '$'}{amountDisplay} {posSettings.fiatCurrency}
          </span>
          {(cryptoAmount || txData.amount) && (
            <span className="status-amount-secondary">
              ≈ {cryptoAmount || txData.amount} {txData.genericToken || posSettings.genericToken}
            </span>
          )}
        </div>
      </div>




       {/* Transaction ID (only if available) */}
      {txData.hash && txData.hash !== 'Processing' && (
        <div className="status-detail-enhanced">
          <span className="status-label-enhanced">{t.transactionId}:</span>
          <span className="status-value-enhanced" style={{ 
            fontSize: '11px', 
            wordBreak: 'break-all', 
            fontFamily: 'monospace' 
          }}>
            {txData.hash.substring(0, 16)}...
          </span>
        </div>
      )}

      {/* Chain Info (if available) */}
      {txData.sourceChain && (
        <div className="status-detail-enhanced">
          <span className="status-label-enhanced">{t.chain}:</span>
          <span className="status-value-enhanced">
            {txData.sourceChain}
          </span>
        </div>
      )}
    </div>
  </div>
)}






  


<div className="action-buttons">
  {currentScreen === 'setup' && (
    <button 
      className="btn-primary-action" 
      onClick={generateQRCode}
      disabled={isCreatingPayment}
      style={{
        opacity: isCreatingPayment ? 0.6 : 1,
        cursor: isCreatingPayment ? 'not-allowed' : 'pointer'
      }}
    >
      {isCreatingPayment ? t.generatingPayment : t.generateQR}
    </button>
  )}

  {/* ✅ Complete button only for CONFIRMED payments */}
  {currentScreen === 'status' && txData && txData.status === 'confirmed' && (
    <button className="btn-primary-action" onClick={resetTransaction}>
      {t.completeTransaction}
    </button>
  )}

  {/* ✅ NEW: Complete button for DELAYED payments */}
  {currentScreen === 'status' && txData && txData.status === 'delayed' && (
    <button className="btn-primary-action" onClick={resetTransaction}>
      {lang === 1 ? 'Nueva Transacción' : 'New Transaction'}
    </button>
  )}
</div>




      {showHistory && posSettings && (
        <HistoryScreen
          tokenId={posSettings.tokenId}
          genericToken={posSettings.genericToken}
          lang={lang}
          onClose={() => setShowHistory(false)}
        />
      )}
    </div>
  );
}

export default Qrcode;